import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { PlusCircle, Save2, Trash, Calendar3, Receipt, Coin, Diagram3, Cart3, CardList, ListTask, Copy } from 'react-bootstrap-icons'
import { Button, Form, Row, Col } from 'react-bootstrap'
import SearchSelect from '../components/form/SearchSelect'
import InputNumber from '../components/form/InputNumber'
import InputDate from '../components/form/InputDate'
import ExpensesProjectsService from '../services/ExpensesProjectsService'
import ProjectsService from '../services/ProjectsService'
import ProvidersService from '../services/ProvidersService'
import { AlertMsg, AlertError, AlertLoading } from '../helpers/AlertHelper'
import { formatDate } from "../helpers/momentHelper"
import expensesConfig from '../config/expenses'

const ExpensesProjectsForm = (props) => {

    const intl = useIntl()
    const [elementId, setElementId] = useState(props.elementId)
    const [action, setAction] = useState(props.action)
    const [projectId, setProjectId] = useState('')
    const [projectsList, setProjectsList] = useState([])
    const [projectErrorKey, setProjectErrorKey] = useState('')
    const [providerId, setProviderId] = useState('')
    const [providersList, setProvidersList] = useState([])
    const [providerErrorKey, setProviderErrorKey] = useState('')
    const [expenseNumber, setExpenseNumber] = useState('')
    const [type, setType] = useState('')
    const [concept, setConcept] = useState('')
    const [date, setDate] = useState(formatDate(Date.now(), 'YYYY-MM-DD'))
    const [paymentDate, setPaymentDate] = useState(formatDate(Date.now(), 'YYYY-MM-DD'))
    const [amount, setAmount] = useState()
    const [buttonTitle, setButtonTitle] = useState('')
    const [inputDisabled, setInputDisabled] = useState(false)
    const [formValid, setFormValid] = useState(false)

    const [showMsg, setShowMsg] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(false)

    // Fetch projects
    const fetchProjects = async () => {
        try {
            const response = await ProjectsService.getProjects(1, '', 2000)
            setProjectsList(response.data.projects)
        } catch (err) {
            setError(err)
        }
    }

    // Fetch providers
    const fetchProviders = async () => {
        try {
            const response = await ProvidersService.getProviders(1, '', 2000)
            setProvidersList(response.data.providers)
        } catch (err) {
            setError(err)
        }
    }

    const getData = async () => {
        await ExpensesProjectsService.getExpenseProject(elementId).then(
            response => {
                setProjectId(response.data.projectId || '')
                setProviderId(response.data.providerId || '')
                setExpenseNumber(response.data.expenseNumber || '')
                setType(response.data.type || '')
                setConcept(response.data.concept || '')
                setDate(formatDate(response.data.date, 'YYYY-MM-DD'))
                setPaymentDate(formatDate(response.data.paymentDate, 'YYYY-MM-DD'))
                setAmount(response.data.amount || 0)
                setIsLoaded(true)
            },
            error => {
                setError(error)
            })
    }

    useEffect(() => {
        fetchProjects()
        fetchProviders()

        if (elementId) {
            getData()
        }

        switch (action) {
            case 'edit':
                setButtonTitle(<FormattedMessage id="textSave" defaultMessage="Save" />)
                break
            case 'delete':
                setButtonTitle(<FormattedMessage id="textDelete" defaultMessage="Delete" />)
                setInputDisabled(true)
                break
            case 'duplicate':
                setButtonTitle(<FormattedMessage id="textDuplicate" defaultMessage="Duplicate" />)
                break
            default:
                setButtonTitle(<FormattedMessage id="textAdd" defaultMessage="Add" />)
        }

    }, [])

    const onSubmit = () => {
        props.onSubmit()
    }

    const handleInputChange = (e) => {
        const name = e.target.name
        const value = e.target.value
        if (name === 'projectId') {
            setProjectErrorKey('')
        }
        if (name === 'providerId') {
            setProviderErrorKey('')
        }
        setFieldValue(name, value)
    }

    const setFieldValue = (fieldName, value) => {
        switch (fieldName) {
            case 'projectId':
                setProjectId(value)
                break
            case 'providerId':
                setProviderId(value)
                break
            case 'expenseNumber':
                setExpenseNumber(value)
                break
            case 'type':
                setType(value)
                break
            case 'concept':
                setConcept(value)
                break
            case 'date':
                setDate(value)
                break
            case 'paymentDate':
                setPaymentDate(value)
                break
            case 'amount':
                setAmount(value)
                break
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        setProjectErrorKey('')
        setProviderErrorKey('')

        const form = e.currentTarget
        let finalProjectId = projectId
        let finalProviderId = providerId
        let expenseProject = {}

        if (action !== "delete") {
            const matchedProject = projectsList.find(p => p.name.toLowerCase() === (projectId?.toString().toLowerCase() || '') || p._id === projectId)
            if (matchedProject) finalProjectId = matchedProject._id

            const matchedProvider = providersList.find(p => p.name.toLowerCase() === (providerId?.toString().toLowerCase() || '') || p._id === providerId)
            if (matchedProvider) finalProviderId = matchedProvider._id

            const isProjectEmpty = !projectId || projectId.toString().trim() === ''
            const isProjectInvalid = isProjectEmpty || !/^[0-9a-fA-F]{24}$/.test(finalProjectId)
            
            const isProviderEmpty = !providerId || providerId.toString().trim() === ''
            const isProviderInvalid = isProviderEmpty || !/^[0-9a-fA-F]{24}$/.test(finalProviderId)
            
            if (!form.checkValidity() || isProjectInvalid || isProviderInvalid || isProjectEmpty || isProviderEmpty) {
                setFormValid(true)
                if (isProjectInvalid){
                    setProjectErrorKey('expensesProjectNotFound2')
                } 
                if (isProviderInvalid){
                    setProviderErrorKey('expensesProviderNotFound')
                } 
                if (isProjectEmpty) {
                    setProjectErrorKey('expensesProjectInvalid')
                }
                if (isProviderEmpty) {
                    setProviderErrorKey('expensesProviderInvalid')
                }
                return
            }

            expenseProject = {
                projectId: finalProjectId,
                providerId: finalProviderId,
                expenseNumber,
                type,
                concept,
                date,
                paymentDate,
                amount
            }
        }

        try {
            if (elementId && action === "edit") {
                await ExpensesProjectsService.updateExpenseProject(expenseProject, elementId)
            } else if (action === "add" || action === "duplicate") {
                await ExpensesProjectsService.addExpenseProject(expenseProject)
            } else if (action === "delete") {
                await ExpensesProjectsService.deleteExpenseProject(elementId)
            }

            setFormValid(false)
            onSubmit()
        } catch (error) {
            const serverError = error.response?.data
            if (serverError?.error?.message?.includes("Cast to ObjectId failed")) {
                setProjectErrorKey('expensesProjectNotFound')
                setFormValid(true)
            } else {
                setShowMsg({ text: serverError?.msg || "Error", style: 'danger' })
                setShowAlert(true)
            }
        } 
    }

    if (error) {
        return (
            <AlertError error={error} />
        )
    } else if (elementId && !isLoaded) {
        return (
            <AlertLoading isLoaded={isLoaded} />
        )
    }

    return (
        <div>
            <Form noValidate validated={formValid} onSubmit={handleSubmit}>
                {elementId && (
                    <Form.Group className="mb-3" controlId="id">
                        <Form.Label className='fw-semibold'>Id:</Form.Label>
                        <Form.Control
                            type="text"
                            name="id"
                            value={elementId}
                            disabled={"disabled"}
                        />
                    </Form.Group>
                )}

                <Form.Group className="mt-4 mb-4" controlId="projectId">
                    <Diagram3 /> <Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="expensesProject" defaultMessage="Project" />:</Form.Label>

                    <SearchSelect
                        fieldName={'projectId'}
                        selected={projectId}
                        onChange={handleInputChange}
                        options={projectsList}
                        disabled={projectsList.length === 0}
                        className={projectErrorKey !== '' ? 'is-invalid border-danger' : ''}
                        placeholder={projectsList.length === 0 ? intl.formatMessage({ id: "expensesProjectsNotFound", defaultMessage: "No projects available" }) : intl.formatMessage({ id: "textClickSelect", defaultMessage: "Click to select or search" })}
                    />

                    {projectErrorKey !== '' && (
                        <div className="text-danger small mt-1">
                            <FormattedMessage id={projectErrorKey} />
                        </div>
                    )}
                </Form.Group>

                <Form.Group className="mb-3" controlId="providerId">
                    <Cart3 /> <Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="expensesProvider" defaultMessage="Provider" />:</Form.Label>

                    <SearchSelect
                        fieldName={'providerId'}
                        selected={providerId}
                        onChange={handleInputChange}
                        options={providersList}
                        disabled={providersList.length === 0}
                        className={providerErrorKey !== '' ? 'is-invalid border-danger' : ''}
                        placeholder={providersList.length === 0 ? intl.formatMessage({ id: "expensesProvidersNotFound", defaultMessage: "No providers available" }) : intl.formatMessage({ id: "textClickSelect", defaultMessage: "Click to select or search" })}
                    />

                    {providerErrorKey !== '' && (
                        <div className="text-danger small mt-1">
                            <FormattedMessage id={providerErrorKey} />
                        </div>
                    )}
                </Form.Group>

                <Row className="mt-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="expenseNumber">
                            <CardList /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="expensesNumber" defaultMessage="Expense number" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="expenseNumber"
                                value={expenseNumber}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}

                                required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="expensesNumberInvalid" defaultMessage="Provide an expense number" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="amount">
                            <Coin /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="expensesAmount" defaultMessage="Amount" />:</Form.Label>
                            <InputNumber
                                fieldName={"amount"}
                                value={amount}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="expensesAmountInvalid" defaultMessage="Provide an amount" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-4" controlId="type">
                    <ListTask /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="expensesType" defaultMessage="Type" />:</Form.Label>
                    <Form.Control
                        as="select"
                        name="type"
                        value={type}
                        onChange={(e) => {
                            handleInputChange({ target: { name: e.target.name, value: e.target.value } });
                        }}
                        disabled={inputDisabled && ("disabled")}
                        required
                    >
                        <option key='blank_type' value=''><FormattedMessage id="textSelect" defaultMessage="Select" /> ...</option>
                        {expensesConfig.PROJECT_EXPENSE_TYPES.map((expenseType) => (
                            <option key={expenseType} value={expenseType}>
                                <FormattedMessage id={"expensesProjectType_" + expenseType} defaultMessage={expenseType} />
                            </option>
                        ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="expensesTypeInvalid" defaultMessage="Select an expense type" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4" controlId="concept">
                    <Receipt /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="expensesConcept" defaultMessage="Concept" />:</Form.Label>
                    <Form.Control
                        type="text"
                        name="concept"
                        value={concept}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                        required="required"
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="expensesConceptInvalid" defaultMessage="Provide a concept" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Row className="mt-4 mb-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="date">
                            <Calendar3 /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="expensesDate" defaultMessage="Expense date" />:</Form.Label>
                            <InputDate
                                fieldName='date'
                                value={date}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="expensesDateInvalid" defaultMessage="Provide an expense date" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="paymentDate">
                            <Calendar3 /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="expensesPaymentDate" defaultMessage="Payment date" />:</Form.Label>
                            <InputDate
                                fieldName='paymentDate'
                                value={paymentDate}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="expensesPaymentDateInvalid" defaultMessage="Provide a payment date" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <AlertMsg message={showMsg.text} variant={showMsg.style} dismissible onCloseHandler={() => setShowAlert(false)} show={showAlert} />

                <div className="d-grid gap-2">
                    {action === "add" && (
                        <Button
                            className="btn btn-dark fw-semibold"
                            type="submit"
                        >
                            <PlusCircle /> {buttonTitle}
                        </Button>
                    )}

                    {action === "edit" && (
                        <Button
                            className="btn btn-primary fw-semibold"
                            type="submit"
                        >
                            <Save2 /> {buttonTitle}
                        </Button>
                    )}

                    {action === "delete" && (
                        <Button
                            className="btn btn-danger fw-semibold"
                            type="submit"
                        >
                            <Trash /> {buttonTitle}
                        </Button>
                    )}
                    {action === "duplicate" && (
                        <Button className="btn btn-dark fw-semibold" type="submit">
                            <Copy /> {buttonTitle}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    )

}

export default ExpensesProjectsForm